import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message } from "antd";
import {
  getAllModulesByCourseId,
  getCertificate,
  getTrainingAssessmentById,
} from "../services/ApiActions/lmsAct";
import Loader from "../components/Layout/Loader";
import AssessmentList from "../components/LMSComponent/AssesmentList";
import CertificateCard from "../components/LMSComponent/CertificateCard";
import PageHeading from "../components/Layout/PageHeading";
import PageSection from "../components/LMSComponent/PageSection";
import LMSBreadcrumb from "../components/LMSComponent/LMSBreadcrumb";
import LMSModules from "./LMS/LMSModules";
import { downloadFile } from "../helper/UtilityMethods";

// Types
interface Module {
  id: string;
  isLock: boolean;
  // Add other properties as needed
}

interface Assessment {
  id: string;
  name: string;
  is_lock: boolean;
  is_attempted: boolean;
}

interface Certificate {
  is_lock: boolean;
  id?: string;
}

interface CourseData {
  courseName: string;
  modules: Module[];
  assessments: Assessment[];
  certificate: Certificate;
}

interface LMSCoursePageProps {
  isTrainingAssessment?: boolean;
}

/**
 * LMSCoursePage component displays course details including modules,
 * assessments, and certificate options
 */
const LMSCoursePage: React.FC<LMSCoursePageProps> = ({
  isTrainingAssessment = false,
}) => {
  // Hooks
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const [messageApi, contextHolder] = message.useMessage();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [courseData, setCourseData] = useState<CourseData>({
    courseName: "",
    modules: [],
    assessments: [],
    certificate: { is_lock: true },
  });

  // Computed properties
  const isCertificateLocked = courseData.certificate?.is_lock;
  const hasAssessments = courseData.assessments.length > 0;

  // Data fetching
  const fetchCourseData = async () => {
    if (!courseId) return;

    setIsLoading(true);

    try {
      const response: any = isTrainingAssessment
        ? await getTrainingAssessmentById(courseId)
        : await getAllModulesByCourseId(courseId);

      if (response?.status === "success") {
        const courseInfo = response.data;
        sessionStorage.setItem("course_name", courseInfo.courseName);
        sessionStorage.setItem("courses", JSON.stringify(courseInfo));
        setCourseData(courseInfo);
      }
    } catch (error) {
      console.error("Error fetching course data:", error);
      messageApi.error("Failed to load course data.");
    } finally {
      setIsLoading(false);
    }
  };

  // Event handlers
  const handleModuleClick = (module: Module) => {
    if (module.isLock) {
      messageApi.info("Please finish previous module to proceed");
      return;
    }
    navigate(`/lms/course/${courseId}/${module.id}/chapter`);
  };

  const handleAssessmentClick = (
    assessmentId: string,
    reattempt?: boolean,
    courseId?: string
  ) => {
    let url = `/lms/course/${courseId}/assesment/${assessmentId}`;
    if (reattempt) {
      url += "?reattempt=true";
    }
    navigate(url);
  };

  const handleCertificateClick = async (certificateId: string) => {
    try {
      const response: any = await getCertificate(certificateId);
      const downloadInfo = {
        blobData: response,
        contentType: "application/pdf",
        fileNameWithExt: "certificate.pdf",
      };
      downloadFile(downloadInfo);
    } catch (error) {
      console.error("Error downloading certificate:", error);
      messageApi.error("Something went wrong. Please try later.");
    }
  };

  // Effects
  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  // Loading state
  if (isLoading) {
    return <Loader style={{ marginTop: "20%", justifyContent: "center" }} />;
  }

  return (
    <div className="lms-course-page">
      {contextHolder}

      <PageHeading
        heading={courseData.courseName}
        onBackClick={() => navigate("/lms")}
      />

      <LMSBreadcrumb courseName={courseData.courseName} />

      <LMSModules onModuleClick={handleModuleClick} courseData={courseData} />

      <PageSection
        heading="Assessment"
        description={
          hasAssessments
            ? "Complete all the modules to unlock the assessments."
            : "No assessment found for this course."
        }
      >
        <AssessmentList
          assessments={courseData.assessments}
          onAssesmentCardClick={(id) =>
            handleAssessmentClick(id, undefined, courseId)
          }
          onReattemptClick={(id) => handleAssessmentClick(id, true, courseId)}
        />
      </PageSection>

      <PageSection
        heading="Certificate"
        description={
          isCertificateLocked
            ? "Your certificate will be available for download once your assignment has been reviewed."
            : undefined
        }
      >
        <CertificateCard
          isLock={isCertificateLocked}
          id={courseId || ""}
          onAssesmentCardClick={handleCertificateClick}
        />
      </PageSection>
    </div>
  );
};

export default LMSCoursePage;
